import { useState, useEffect } from "react";
import { API , authFetch } from "@/App";
import { toast } from "sonner";
import { Bell, Plus, Send, Volume2, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    body: "",
    notification_type: "SYSTEM_ALERT",
    target_type: "ALL_EMPLOYEES",
    sound: "SND_004",
  });

  const sounds = [
    { id: "SND_001", name: "Lion Roar Alert", description: "Deep, confident lion sound" },
    { id: "SND_002", name: "Order Confirmed", description: "Warm two-tone chime" },
    { id: "SND_003", name: "Payment Received", description: "Cash register sound" },
    { id: "SND_004", name: "Nudge Ping", description: "Light, friendly ping" },
    { id: "SND_005", name: "Urgent Alert", description: "Pulsing double-beep" },
    { id: "SND_006", name: "Dispatch Horn", description: "Truck horn sound" },
    { id: "SND_007", name: "Delivery Done", description: "Triumphant completion" },
  ];

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await authFetch(`${API}/notifications`, {  });
      const data = await response.json();
      if (data.success) {
        setNotifications(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${API}/notifications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Notification sent!");
        // Play the selected sound
        playSound(formData.sound);
        setIsDialogOpen(false);
        resetForm();
        fetchNotifications();
      } else {
        toast.error(data.error || "Failed to send notification");
      }
    } catch (error) {
      toast.error("Failed to send notification");
    }
  };

  const playSound = (soundId) => {
    // Mock sound playback using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Different sounds based on ID
    const frequencies = {
      SND_001: [200, 300, 400], // Lion roar - low frequencies
      SND_002: [523, 659], // Order confirmed - ascending
      SND_003: [800, 1000, 800], // Payment - cash register
      SND_004: [880], // Nudge ping - simple
      SND_005: [600, 600], // Urgent - double beep
      SND_006: [200, 300], // Dispatch horn - deep
      SND_007: [523, 659, 784], // Delivery done - triumphant
    };

    const freqs = frequencies[soundId] || [440];
    oscillator.frequency.value = freqs[0];
    gainNode.gain.value = 0.1;

    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      body: "",
      notification_type: "SYSTEM_ALERT",
      target_type: "ALL_EMPLOYEES",
      sound: "SND_004",
    });
  };

  const getTypeBadge = (type) => {
    const styles = {
      SYSTEM_ALERT: "bg-blue-100 text-blue-700 border-blue-200",
      DEALER_NUDGE: "bg-purple-100 text-purple-700 border-purple-200",
    };
    return (
      <Badge variant="outline" className={styles[type] || "bg-slate-100 text-slate-700"}>
        {type === "DEALER_NUDGE" ? "Dealer Nudge" : "System Alert"}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Notifications</h1>
          <p className="text-slate-500 text-sm mt-1">
            Send alerts and nudges to your team and dealers
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setIsDialogOpen(true);
          }}
          className="btn-primary"
          data-testid="send-notification-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          Send Notification
        </Button>
      </div>

      {/* Sound Library Card */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-[#8B1A1A]/10">
            <Volume2 className="w-5 h-5 text-[#8B1A1A]" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Pravaah Sound Library</h3>
            <p className="text-sm text-slate-500">
              Custom notification sounds for the Lion Cement brand
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {sounds.map((sound) => (
            <button
              key={sound.id}
              onClick={() => playSound(sound.id)}
              className="p-3 rounded-lg border border-slate-200 hover:border-[#8B1A1A] hover:bg-[#8B1A1A]/5 transition-colors text-left group"
              data-testid={`sound-${sound.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Volume2 className="w-4 h-4 text-slate-400 group-hover:text-[#8B1A1A]" />
                <span className="font-mono text-xs text-slate-400">{sound.id}</span>
              </div>
              <p className="font-medium text-sm text-slate-900">{sound.name}</p>
              <p className="text-xs text-slate-500">{sound.description}</p>
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-4">
          Click any sound to preview. Sounds play on dealer/employee phones when notifications are received.
        </p>
      </Card>

      {/* Notifications List */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-slate-900">Notification History</h3>
        </div>
        <div className="divide-y">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 spinner mx-auto"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8">
              <div className="empty-state">
                <Bell className="empty-state-icon" />
                <p className="empty-state-title">No notifications sent yet</p>
                <p className="empty-state-description">
                  Send your first notification to dealers or employees.
                </p>
                <Button onClick={() => setIsDialogOpen(true)} className="btn-primary">
                  <Send className="w-4 h-4 mr-2" />
                  Send Notification
                </Button>
              </div>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.notification_id}
                className="p-4 hover:bg-slate-50 transition-colors"
                data-testid={`notification-${notification.notification_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getTypeBadge(notification.notification_type)}
                      <span className="text-xs text-slate-400">
                        {sounds.find((s) => s.id === notification.sound)?.name || notification.sound}
                      </span>
                    </div>
                    <h4 className="font-medium text-slate-900">{notification.title}</h4>
                    <p className="text-sm text-slate-500 mt-1">{notification.body}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span>Target: {notification.target_type}</span>
                      <span>Delivered: {notification.delivered_count}</span>
                      <span>Read: {notification.read_count}</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400">
                    {notification.sent_at
                      ? new Date(notification.sent_at).toLocaleDateString()
                      : "Pending"}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Send Notification Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="notification-dialog">
          <DialogHeader>
            <DialogTitle>Send Notification</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Tabs
              value={formData.notification_type}
              onValueChange={(v) => setFormData({ ...formData, notification_type: v })}
            >
              <TabsList className="w-full">
                <TabsTrigger value="SYSTEM_ALERT" className="flex-1">
                  System Alert
                </TabsTrigger>
                <TabsTrigger value="DEALER_NUDGE" className="flex-1">
                  Dealer Nudge
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div>
              <label className="text-sm font-medium text-slate-700">
                Title / Hook Line <span className="text-slate-400">(max 65 chars)</span>
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={
                  formData.notification_type === "DEALER_NUDGE"
                    ? "New rate. Same trust. 🦁"
                    : "Important update from Pravaah"
                }
                maxLength={65}
                required
              />
              <p className="text-xs text-slate-400 mt-1">
                {formData.title.length}/65 characters
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">
                Body / Message <span className="text-slate-400">(max 180 chars)</span>
              </label>
              <Textarea
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="Enter your message..."
                maxLength={180}
                rows={3}
                required
              />
              <p className="text-xs text-slate-400 mt-1">
                {formData.body.length}/180 characters
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Target Audience</label>
                <Select
                  value={formData.target_type}
                  onValueChange={(v) => setFormData({ ...formData, target_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL_EMPLOYEES">All Employees</SelectItem>
                    <SelectItem value="ALL_DEALERS">All Dealers</SelectItem>
                    <SelectItem value="ROLE">By Role</SelectItem>
                    <SelectItem value="BEAT">By BEAT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Sound</label>
                <Select
                  value={formData.sound}
                  onValueChange={(v) => setFormData({ ...formData, sound: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sounds.map((sound) => (
                      <SelectItem key={sound.id} value={sound.id}>
                        {sound.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Phone Preview */}
            <div className="p-4 bg-slate-900 rounded-xl">
              <p className="text-xs text-slate-400 mb-2">Preview</p>
              <div className="bg-slate-800 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded bg-[#8B1A1A] flex items-center justify-center">
                    <img
                      src="https://avicement.com/logo.png"
                      alt=""
                      className="w-3 h-3"
                    />
                  </div>
                  <span className="text-xs text-slate-400">Pravaah</span>
                  <span className="text-xs text-slate-500">now</span>
                </div>
                <p className="text-white text-sm font-medium">
                  {formData.title || "Notification title"}
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  {formData.body || "Notification message will appear here..."}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                <Send className="w-4 h-4 mr-2" />
                Send Now
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsPage;
